import { useMemo, useState } from 'react';
import { useMerchantNetwork } from '@/api/hooks/useMerchants';
import DetectiveChips from '@/components/ai/DetectiveChips';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MapPin, ShieldAlert } from 'lucide-react';

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const RPL = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

export default function MerchantNetwork() {
  const { data, isLoading, error } = useMerchantNetwork();
  const [filter, setFilter] = useState('');
  const [riskOnly, setRiskOnly] = useState(false);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    return data.merchants.filter((m) => {
      if (riskOnly && !m.is_high_risk) return false;
      if (!q) return true;
      const hay = `${m.merchant_code} ${m.trade_name ?? ''} ${m.brand ?? ''} ${m.zone_code ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data, filter, riskOnly]);

  const geoCount = useMemo(
    () => data?.merchants.filter((m) => m.latitude != null && m.longitude != null).length ?? 0,
    [data],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Merchant Network</h1>
        <p className="text-sm text-muted-foreground">
          Active merchants ranked by 90-day spend. {data && (
            <span>
              {NUM.format(data.total_merchants)} merchants · {NUM.format(geoCount)} geocoded
            </span>
          )}
        </p>
      </div>

      <DetectiveChips module="merchant-network" />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by code, brand, name, zone…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        <button
          type="button"
          onClick={() => setRiskOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ${
            riskOnly
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-input bg-background text-muted-foreground hover:bg-accent'
          }`}
        >
          <ShieldAlert className="h-3.5 w-3.5" />
          High-risk only
        </button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load merchant network: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <Card className="flex-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {rows.length === data?.merchants.length
              ? `Top ${rows.length} merchants`
              : `${rows.length} matching merchants`}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead className="text-right">Txns 90d</TableHead>
                  <TableHead className="text-right">Spend 90d</TableHead>
                  <TableHead className="text-right">Avg R/L</TableHead>
                  <TableHead>Geo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.merchant_id}>
                    <TableCell>
                      <div className="font-medium">
                        {m.trade_name ?? m.merchant_code}
                        {m.is_high_risk && (
                          <Badge
                            variant="outline"
                            className="ml-2 border-red-200 bg-red-100 text-red-800"
                          >
                            high-risk
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{m.merchant_code}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.brand ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.zone_code ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {NUM.format(m.txn_count_90d)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ZAR.format(m.spend_90d_rands)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.avg_rand_per_litre_90d != null
                        ? `R ${RPL.format(m.avg_rand_per_litre_90d)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {m.latitude != null && m.longitude != null ? (
                        <a
                          href={`https://www.google.com/maps?q=${m.latitude},${m.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <MapPin className="h-3 w-3" />
                          map
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && data && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      No merchants match the current filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
